// 盆栽（植物の実時間成長・樹形・剪定）の純ロジック。M4 の中核。
//
// 設計方針（GAME_DESIGN.md §3 盆栽 / M4）:
//  - 植物デコは実時間で成長段階が進む: 芽 → 若木 → 成木 → 古木。
//  - 成長方向にはランダム性があり、樹形（枝の本数・曲がり・茂り）は seed から決まる。
//    同じセーブなら再現可能（決定的）。
//  - 剪定で樹形を選べる。樹形によって属性が変わる（伸ばし＝緑寄り / 整え＝静寂寄り）。
//  - 古木は最終段階。属性が強くなり庭の象徴になる。
//  - 枯れない。放置で悪いことは一切起きない。
//
// この層は描画・DOM・乱数（Math.random）に触れない純データ＋純関数。成長は
// simulate が経過秒を足し込み、樹形は seed から決定的に導出する。

import { hashSeed, nextRandom } from './rng';
import type { AttributeKey, AttributeVector } from './decos';

/** 成長段階。配列インデックスが進むほど成熟する。 */
export type GrowthStage = 'sprout' | 'sapling' | 'mature' | 'ancient';

/** 成長段階の順序（インデックス＝成熟度）。 */
export const GROWTH_STAGES: readonly GrowthStage[] = [
  'sprout',
  'sapling',
  'mature',
  'ancient',
] as const;

/** 段階の日本語表示名（UI・オフライン精算サマリで使う）。 */
export const STAGE_LABELS: Record<GrowthStage, string> = {
  sprout: '芽',
  sapling: '若木',
  mature: '成木',
  ancient: '古木',
};

/**
 * 各段階に「入る」までに必要な累計成長秒。実時間で進む。
 *
 * 時間設計（放置ゲームらしく、1 日かけて古木まで届く緩やかさ）:
 *  - 0s〜       芽（植えた瞬間）
 *  - 30 分〜    若木
 *  - 2.5 時間〜 成木（若木から +2 時間）
 *  - 10.5 時間〜 古木（成木から +8 時間）
 *
 * 「急かさない・失敗がない」方針なので上限は無く、古木で頭打ち（それ以上育てても
 * 段階は変わらない）。短時間ログインでも芽→若木の変化が見え、1 日放置すれば
 * 古木という象徴に届く、という観察リズムを狙う。
 */
export const STAGE_THRESHOLDS_SEC: Record<GrowthStage, number> = {
  sprout: 0,
  sapling: 1 * 3600, // 1 時間
  mature: 6 * 3600, // 6 時間
  ancient: 24 * 3600, // 24 時間（池×若木シナジー 1.2倍なら最速 ~20h で古木＝nemu/図鑑の capstone）
};

/**
 * 樹形パラメータ。seed から決定的に決まり、剪定で動く。
 * 値域はおおむね 0..1（描画はこれを枝/樹冠の配置に翻訳する）。
 */
export interface TreeForm {
  /** 枝の本数の素（0..1、描画では 2〜5 本程度に量子化）。 */
  branches: number;
  /** 枝の曲がり具合（0=まっすぐ 〜 1=うねる）。 */
  bend: number;
  /** 茂り（樹冠のボリューム。0=まばら 〜 1=こんもり）。 */
  lush: number;
  /**
   * 仕立ての傾き。0 = どちらでもない、正 = 伸ばし寄り（自由）、
   * 負 = 整え寄り（端正）。剪定で ±していく。属性の振り分けに使う。
   */
  trained: number;
}

/** 1 セルに植わった植物の生育状態（GameState.plantings の値）。 */
export interface Planting {
  /** これまでに育った累計秒。simulate が経過ぶん加算する（古木以降も貯まるが段階は不変）。 */
  growSec: number;
  /** 樹形。種から導出した初期値に、剪定の効果を畳み込んだ現在値。 */
  form: TreeForm;
}

/** 植物として成長するデコ ID（ここに無いデコは従来どおり成長しない）。 */
export const PLANT_DECO_IDS: ReadonlySet<string> = new Set(['sapling', 'violet_bed']);

/** デコが成長する植物か。 */
export function isPlantDeco(decoId: string): boolean {
  return PLANT_DECO_IDS.has(decoId);
}

/**
 * 累計成長秒から現在の成長段階を返す。閾値以上で次段階に入る。
 * 古木で頭打ち（枯れない・退行しない）。
 */
export function stageForGrowSec(growSec: number): GrowthStage {
  let stage: GrowthStage = 'sprout';
  for (const s of GROWTH_STAGES) {
    if (growSec >= STAGE_THRESHOLDS_SEC[s]) stage = s;
  }
  return stage;
}

/** 古木かどうか（レア客条件・象徴判定に使う近道）。 */
export function isAncient(growSec: number): boolean {
  return growSec >= STAGE_THRESHOLDS_SEC.ancient;
}

/**
 * 植えるセル・デコ・seed から初期樹形を決定的に導出する。
 *
 * セルごとに別の樹形になるよう「セル index」も種に混ぜる（同じ庭でも木ごとに
 * 違う枝ぶりにする）。同じセーブ（同じ seed・同じ場所）なら必ず同じ形になる。
 */
export function deriveInitialForm(seed: number, decoId: string, cellIndex: number): TreeForm {
  // セル・デコ・ゲーム seed を畳み込んで木ごとの基種を作る。
  const base = (hashSeed(`${decoId}:${cellIndex}`) ^ (seed >>> 0)) >>> 0;
  const r1 = nextRandom(base);
  const r2 = nextRandom(r1.next);
  const r3 = nextRandom(r2.next);
  return {
    branches: r1.value,
    bend: r2.value,
    lush: r3.value,
    trained: 0, // 仕立てはまだニュートラル
  };
}

/** 新しく植えた直後の Planting（成長 0・seed 由来の樹形）。 */
export function createPlanting(seed: number, decoId: string, cellIndex: number): Planting {
  return { growSec: 0, form: deriveInitialForm(seed, decoId, cellIndex) };
}

/** 剪定の種類。 */
export type PruneStyle = 'extend' | 'tidy';

/** 剪定の表示名。 */
export const PRUNE_LABELS: Record<PruneStyle, string> = {
  extend: 'のばす剪定',
  tidy: 'ととのえる剪定',
};

/** 1 回の剪定で樹形を動かす量。 */
const PRUNE_STEP = 0.22;
/** trained のクランプ範囲。 */
const TRAINED_MIN = -1;
const TRAINED_MAX = 1;

function clamp01(v: number): number {
  return v < 0 ? 0 : v > 1 ? 1 : v;
}
function clamp(v: number, lo: number, hi: number): number {
  return v < lo ? lo : v > hi ? hi : v;
}

/**
 * 樹形に剪定を 1 回かけた新しい樹形を返す（純関数・入力は破壊しない）。
 *
 *  - のばす（extend）: 枝を伸ばし茂らせる。branches↑ lush↑ bend↑ trained を正へ。
 *    → 属性は「緑」寄りになる。
 *  - ととのえる（tidy）: 端正に刈り込む。lush↓ bend↓（まっすぐに）trained を負へ。
 *    → 属性は「静寂」寄りになる。
 *
 * 剪定は無料・いつでも・失敗なし（盆栽的試行錯誤を阻害しない）。
 */
export function pruneForm(form: TreeForm, style: PruneStyle): TreeForm {
  if (style === 'extend') {
    return {
      branches: clamp01(form.branches + PRUNE_STEP * 0.5),
      bend: clamp01(form.bend + PRUNE_STEP * 0.4),
      lush: clamp01(form.lush + PRUNE_STEP),
      trained: clamp(form.trained + PRUNE_STEP, TRAINED_MIN, TRAINED_MAX),
    };
  }
  // tidy
  return {
    branches: clamp01(form.branches - PRUNE_STEP * 0.25),
    bend: clamp01(form.bend - PRUNE_STEP),
    lush: clamp01(form.lush - PRUNE_STEP * 0.6),
    trained: clamp(form.trained - PRUNE_STEP, TRAINED_MIN, TRAINED_MAX),
  };
}

/**
 * 植物 1 本の現在の属性ベクトルを返す。デコ自体の基礎属性に、成長段階と樹形の
 * ぶんを上乗せする（gardenAttributes がこれを合算する）。
 *
 * 設計（GAME_DESIGN.md 準拠）:
 *  - 成長で「緑」が段階的に増える（芽は控えめ、古木で最大）。
 *  - 仕立て trained が正（伸ばし）なら緑をさらに、負（整え）なら静寂を増やす。
 *  - 古木は象徴なので静寂を少し底上げ（佇まいの落ち着き）。
 *
 * @param baseAttributes デコ定義の基礎属性（spread して足す土台）。
 * @param growSec        累計成長秒。
 * @param form           現在の樹形。
 */
export function plantAttributes(
  baseAttributes: AttributeVector,
  growSec: number,
  form: TreeForm,
): AttributeVector {
  const stage = stageForGrowSec(growSec);
  const stageIdx = GROWTH_STAGES.indexOf(stage); // 0..3
  const out: Partial<Record<AttributeKey, number>> = { ...baseAttributes };

  const add = (key: AttributeKey, v: number): void => {
    if (v === 0) return;
    out[key] = (out[key] ?? 0) + v;
  };

  // 成長段階ぶんの緑（芽0 / 若木1 / 成木2 / 古木3）。
  add('green', stageIdx);

  // 仕立てによる振り分け。trained>0 を伸ばし（緑）、<0 を整え（静寂）に。
  // 段階が進むほど仕立ての効きを強める（成熟した木ほど個性が出る）。
  const trainMag = Math.round(Math.abs(form.trained) * (1 + stageIdx)); // 0..(1+idx)
  if (form.trained > 0) add('green', trainMag);
  else if (form.trained < 0) add('calm', trainMag);

  // 古木は象徴。静寂を底上げして「佇まい」を出す。
  if (stage === 'ancient') add('calm', 1);

  return out;
}
